import React, { useState, useEffect } from 'react';
import { useConfirm } from '../context/ConfirmContext';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Person, ContactType, Customer } from '../types';
import { personsAPI, customersAPI } from '../services/api';
import { addRedirectToUrl } from '../utils/redirectUtils';

const PersonDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [person, setPerson] = useState<Person | null>(null);
  const [customerGroupMap, setCustomerGroupMap] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const confirm = useConfirm();

  useEffect(() => {
    if (id) {
      loadPerson(parseInt(id));
    }
  }, [id]);

  useEffect(() => {
    loadCustomerGroups();
  }, []);

  const loadPerson = async (personId: number) => {
    try {
      setLoading(true);
      const data = await personsAPI.getPerson(personId);
      setPerson(data);
      setError(null);
    } catch (err) {
      setError('Failed to load person details');
      console.error('Error loading person:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomerGroups = async () => {
    try {
      const customers = await customersAPI.getCustomers();
      const map = customers.reduce<Record<number, string>>((accumulator, customer: Customer) => {
        if (customer.group) {
          accumulator[customer.id] = customer.group;
        }
        return accumulator;
      }, {});
      setCustomerGroupMap(map);
    } catch (error) {
      console.error('Error loading customer groups:', error);
    }
  };

  const getContactTypeBadge = (contactType: ContactType) => {
    switch (contactType) {
      case ContactType.Hot:
        return (
          <span
            className="badge contact-type-badge"
            style={{ backgroundColor: '#374151', color: '#fff' }}
          >
            Hot
          </span>
        );
      case ContactType.Cold:
        return (
          <span
            className="badge contact-type-badge"
            style={{ backgroundColor: '#6b7280', color: '#fff' }}
          >
            Cold
          </span>
        );
      default:
        return (
          <span
            className="badge contact-type-badge"
            style={{ backgroundColor: '#6b7280', color: '#fff' }}
          >
            Unknown
          </span>
        );
    }
  };

  const getContactTypeMeta = (contactType: ContactType) => {
    switch (contactType) {
      case ContactType.Hot:
        return { icon: 'fa-solid fa-fire', label: 'Hot', color: '#dc2626' };
      case ContactType.Cold:
        return { icon: 'fa-solid fa-snowflake', label: 'Cold', color: '#2563eb' };
      default:
        return { icon: 'fa-solid fa-circle-question', label: 'Unknown', color: '#6b7280' };
    }
  };


  const handleEdit = () => {
    navigate(addRedirectToUrl(`/persons/edit/${id}`, location.pathname + location.search));
  };

  const handleDelete = async () => {
    if (!person) return;
    const isConfirmed = await confirm({
      title: 'Delete person',
      message: 'Are you sure you want to delete this person? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger'
    });
    if (!isConfirmed) return;

    try {
      await personsAPI.deletePerson(person.id);
      navigate('/persons');
    } catch (err) {
      setError('Failed to delete person');
      console.error('Error deleting person:', err);
    }
  };

  if (loading) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <div className="spinner-border" role="status" />
        <span className="visually-hidden">Loading...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>
                Back to Persons
        </button>
      </div>
    );
  }

  if (!person) {
    return (
      <div className="container-fluid">
        <div className="alert alert-warning" role="alert">
          Person not found
        </div>
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>
                Back to Persons
        </button>
      </div>
    );
  }

  const contactMeta = getContactTypeMeta(person.contactType);

  return (
    <div className="container-fluid person-details-page">
      <div className="row mb-4 person-details-header">
        <div className="col-md-8">
          <h1 className="person-details-title">
            <i className="fa-solid fa-user" aria-hidden="true" style={{ marginRight: '0.5rem' }} />
            {person.firstName} {person.lastName}
          </h1>
          <p className="text-muted mb-0 d-flex align-items-center gap-2">
            <i className={contactMeta.icon} aria-hidden="true" style={{ color: contactMeta.color }} />
            {contactMeta.label}
          </p>
        </div>
        <div className="col-md-4 text-end">
          <div className="btn-group person-details-actions">
            <button
              className="btn btn-primary"
              onClick={handleEdit}
            >
              Edit
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => navigate(-1)}
            >
              Back
            </button>
            <button
              className="btn btn-danger"
              onClick={handleDelete}
            >
              Delete
            </button>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card mb-4 person-details-card">
            <div className="card-header">
              <h5 className="card-title mb-0">Basic & Contact Information</h5>
            </div>
            <div className="card-body">
              <div className="row g-2">
                <div className="col-md-6 mb-2">
                  <label className="form-label fw-bold">Contact Type:</label>
                  <p className="mb-1">{getContactTypeBadge(person.contactType)}</p>
                </div>
                <div className="col-md-6 mb-2">
                  <label className="form-label fw-bold">Organizations:</label>
                  {person.customers && person.customers.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-sm align-middle mb-0">
                        <thead>
                          <tr>
                            <th className="text-start">Company</th>
                            <th className="text-start">Email</th>
                            <th className="text-start">Role</th>
                            <th className="text-start">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...person.customers]
                            .sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())
                            .map((cp) => (
                              <tr key={cp.id} className={cp.isFormer ? 'table-secondary' : undefined}>
                                <td>
                                  <div className="fw-bold">{cp.customerTitle}</div>
                                  {customerGroupMap[cp.customerId] && (
                                    <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                                      {customerGroupMap[cp.customerId]}
                                    </div>
                                  )}
                                  {cp.isFormer && (
                                    <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                                      Former
                                    </div>
                                  )}
                                </td>
                                <td>
                                  {cp.workingEmail ? (
                                    <a
                                      href={`mailto:${cp.workingEmail}`}
                                      className="text-decoration-underline"
                                      style={{ color: 'var(--primary)', textDecorationColor: 'var(--primary)' }}
                                    >
                                      {cp.workingEmail}
                                    </a>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                                <td>{cp.jobTitle || cp.department || '—'}</td>
                                <td>
                                  <button
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={handleEdit}
                                    title="Edit"
                                  >
                                    <i className="fa-solid fa-pen" aria-hidden="true" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-muted mb-1">No employers assigned</p>
                  )}
                </div>
                <div className="col-md-6 mb-2">
                  <label className="form-label fw-bold">Email Addresses:</label>
                  {person.emails.length > 0 ? (
                    <div className="d-flex flex-column gap-1">
                      {person.emails.map((email, index) => (
                        <a
                          key={index}
                          href={`mailto:${email.address}`}
                          className="text-decoration-underline"
                          style={{ color: 'var(--primary)', textDecorationColor: 'var(--primary)', fontWeight: 600 }}
                        >
                          {email.address}
                        </a>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted mb-1">No email addresses</p>
                  )}
                </div>
                <div className="col-md-6 mb-2">
                  <label className="form-label fw-bold">Phone Numbers:</label>
                  {person.phones.length > 0 ? (
                    <div className="d-flex flex-column gap-1">
                      {person.phones.map((phone, index) => (
                        <a
                          key={index}
                          href={`tel:${phone.number}`}
                          className="text-decoration-underline"
                          style={{ color: 'var(--primary)', textDecorationColor: 'var(--primary)', fontWeight: 600 }}
                        >
                          {phone.number}
                        </a>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted mb-1">No phone numbers</p>
                  )}
                </div>
                {person.linkedInUrl && (
                  <div className="col-md-6 mb-2">
                    <label className="form-label fw-bold">LinkedIn:</label>
                    <p className="mb-1">
                      <a
                        href={person.linkedInUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-decoration-underline"
                        style={{ color: 'var(--primary)', textDecorationColor: 'var(--primary)', fontWeight: 600 }}
                      >
                        {person.linkedInUrl}
                      </a>
                    </p>
                  </div>
                )}
                <div className="col-md-6 mb-2">
                  <label className="form-label fw-bold">Social Links:</label>
                  {person.socialLinks.length > 0 ? (
                    <div className="d-flex flex-column gap-1">
                      {person.socialLinks.map((link, index) => (
                        <a
                          key={index}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-decoration-underline"
                          style={{ color: 'var(--primary)', textDecorationColor: 'var(--primary)', fontWeight: 600 }}
                        >
                          {link.url}
                        </a>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted mb-1">No social links</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {person.notes && (
            <div className="card mb-4 person-details-card">
              <div className="card-header">
                <h5 className="card-title mb-0">Notes</h5>
              </div>
              <div className="card-body markdown-content">
                <ReactMarkdown>{person.notes}</ReactMarkdown>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default PersonDetails;
